import { colors } from '@src/styles';

export const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";
export const monoFontFamily = "'JetBrains Mono', monospace";

export const CONTROL_HEIGHT = 36;

export const monoLabelSX = {
  fontFamily: monoFontFamily,
  fontSize: '10px',
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: colors.brand.textMuted,
} as const;

export const skeletonSX = { backgroundColor: 'rgba(255, 255, 255, 0.06)' } as const;

export const filterFieldSX = {
  '& .MuiOutlinedInput-root': {
    height: CONTROL_HEIGHT,
    fontSize: '13px',
    color: colors.primary.white,
    backgroundColor: colors.brand.surface,
    borderRadius: '8px',
    transition: 'border-color 0.15s ease',
    '& fieldset': { borderColor: colors.brand.line },
    '&:hover fieldset': { borderColor: 'rgba(255, 160, 21, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: colors.primary.yellow, borderWidth: '1px' },
  },
  '& .MuiAutocomplete-inputRoot': { paddingY: '0 !important' },
  '& input': { color: colors.primary.white },
  '& input::placeholder': { color: colors.brand.textMuted, opacity: 1 },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator, & .MuiSelect-icon': {
    color: colors.brand.textSecondary,
    '& svg': { color: 'inherit' },
  },
} as const;

export const dropdownPaperSX = {
  marginTop: 0.5,
  backgroundColor: colors.brand.surface,
  backgroundImage: 'none',
  border: `1px solid ${colors.brand.line}`,
  borderRadius: '10px',
  boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
  color: colors.primary.white,
  '& .MuiAutocomplete-listbox, & .MuiMenu-list': { padding: 0.5 },
  '& .MuiAutocomplete-option, & .MuiMenuItem-root': {
    minHeight: 36,
    borderRadius: '6px',
    fontSize: '13px',
    color: colors.primary.white,
    backgroundColor: 'transparent',
    '&:hover, &.Mui-focused, &.Mui-focusVisible': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
    '&[aria-selected="true"], &.Mui-selected': { backgroundColor: 'rgba(255, 160, 21, 0.12) !important' },
  },
  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': { color: colors.brand.textSecondary, fontSize: '13px' },
} as const;

export const primaryButtonSX = {
  height: CONTROL_HEIGHT,
  px: 2,
  borderRadius: '8px',
  backgroundColor: colors.primary.yellow,
  color: colors.brand.ink,
  fontFamily: titleFontFamily,
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '& .MuiButton-startIcon svg': { fontSize: 18, color: 'inherit' },
  '&:hover': { backgroundColor: '#FFB94A', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: 'rgba(255, 160, 21, 0.25)', color: 'rgba(10, 13, 15, 0.55)' },
} as const;

export const ghostButtonSX = {
  height: CONTROL_HEIGHT,
  px: 1.75,
  borderRadius: '8px',
  border: `1px solid ${colors.brand.line}`,
  color: colors.brand.textStrong,
  fontFamily: titleFontFamily,
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon svg': { fontSize: 17, color: 'inherit' },
  '&:hover': { borderColor: 'rgba(255, 160, 21, 0.5)', backgroundColor: 'rgba(255, 255, 255, 0.03)' },
  '&.Mui-disabled': { color: colors.brand.textMuted, borderColor: colors.brand.line },
} as const;

export const outlineGoldButtonSX = {
  ...ghostButtonSX,
  border: '1px solid rgba(255, 160, 21, 0.5)',
  backgroundColor: 'rgba(255, 160, 21, 0.08)',
  color: colors.primary.yellow,
  fontWeight: 600,
  '&:hover': { borderColor: colors.primary.yellow, backgroundColor: 'rgba(255, 160, 21, 0.16)' },
} as const;

export const paginationSX = {
  '& .MuiPaginationItem-root': {
    minWidth: 28,
    height: 28,
    borderRadius: '7px',
    fontFamily: monoFontFamily,
    fontSize: '12px',
    color: colors.brand.textSecondary,
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: colors.primary.white },
  },
  '& .MuiPaginationItem-root.Mui-selected': {
    backgroundColor: 'rgba(255, 160, 21, 0.14)',
    color: colors.primary.yellow,
    border: '1px solid rgba(255, 160, 21, 0.45)',
    '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.2)' },
  },
  '& .MuiPaginationItem-icon': { color: 'inherit' },
} as const;

export const calendarPopperSX = {
  '& .MuiPaper-root': {
    backgroundColor: colors.brand.surface,
    backgroundImage: 'none',
    border: `1px solid ${colors.brand.line}`,
    borderRadius: '12px',
    color: colors.primary.white,
  },
  '& .MuiPickersCalendarHeader-label, & .MuiDayCalendar-weekDayLabel': { color: colors.brand.textSecondary },
  '& .MuiPickersCalendarHeader-switchViewButton, & .MuiPickersArrowSwitcher-button': {
    color: colors.brand.textSecondary,
    '& svg': { color: 'inherit' },
  },
  '& .MuiPickersDay-root': {
    color: colors.primary.white,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
    '&.Mui-selected': {
      backgroundColor: `${colors.primary.yellow} !important`,
      color: colors.brand.ink,
    },
    '&.MuiPickersDay-today': { borderColor: colors.primary.yellow },
    '&.Mui-disabled': { color: colors.brand.textMuted },
  },
  '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
    color: colors.primary.white,
    '&.Mui-selected': { backgroundColor: colors.primary.yellow, color: colors.brand.ink },
  },
} as const;

export const blurBackdropSX = { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(10, 13, 15, 0.55)' } as const;

export const noticeSX = (color: string) =>
  ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.25,
    padding: '10px 12px',
    borderRadius: '10px',
    border: `1px solid ${color}40`,
    backgroundColor: `${color}0F`,
    color: colors.brand.textStrong,
    fontSize: '13px',
    lineHeight: 1.5,
    '& svg': { fontSize: 18, color, flexShrink: 0, marginTop: '1px' },
  }) as const;

export const ACTION_BUTTON_HEIGHT = 32;

export const toneButtonSX = (color: string, filled = false) =>
  ({
    height: ACTION_BUTTON_HEIGHT,
    px: 1.5,
    gap: 0.75,
    borderRadius: '8px',
    border: `1px solid ${filled ? color : `${color}66`}`,
    backgroundColor: filled ? color : `${color}14`,
    color: filled ? colors.brand.ink : color,
    fontFamily: titleFontFamily,
    fontSize: '13px',
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '& svg': { fontSize: 16, color: 'inherit' },
    '&:hover': {
      backgroundColor: filled ? color : `${color}26`,
      borderColor: color,
      filter: filled ? 'brightness(1.08)' : 'none',
    },
    '&.Mui-disabled': {
      color: colors.brand.textMuted,
      borderColor: colors.brand.line,
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      cursor: 'not-allowed',
    },
  }) as const;
